import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { customersApi } from 'src/api/endpoints/customers';
import toast from 'react-hot-toast';

interface CreateCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setMobile('');
      setError(null);
      setLoading(false);
      // Autofocus email
      setTimeout(() => {
        emailRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    const trimmedMobile = mobile.trim();
    const isValidMobile = /^\d{10}$/.test(trimmedMobile);
    if (!isValidMobile) {
      setError('Mobile number must be exactly 10 digits.');
      return;
    }

    setLoading(true);

    try {
      const response = await customersApi.createUser(email, mobile);
      
      if (response.status) {
        toast.success('User created successfully!');
        onSuccess();
        onClose();
        setEmail('');
        setMobile('');
        setError(null);
      } else {
        // Handle API error response
        const errorMessage = response.message || 'Failed to create user';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
  
      if(err.message === 403) {
        toast.error('You are not authorized to perform this action. Please contact support.');
      }
      let errorMessage = 'An unexpected error occurred';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error('You are not authorized to perform this action. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobile(e.target.value);
    if (error) setError(null); // ✅ Clear error when user starts typing
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New User</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              inputRef={emailRef}
              value={email}
              onChange={handleEmailChange}
              required
              fullWidth
            />
            <TextField
              label="Mobile"
              value={mobile}
              onChange={handleMobileChange}
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCustomerModal;
